<template>
  <div class="users-table">
    <div class="users-table__table">
      <div v-if="isLoadingMore" class="users-table__scroll-loading">
        <b-spinner variant="primary" />
        <span>Loading...</span>
      </div>
      <el-table
        ref="table"
        :data="tableData"
        v-loading="loading"
        :height="tableHeight"
        style="width: 100%"
        @selection-change="onSelectionChange"
      >
        <!-- Selection -->
        <el-table-column type="selection" width="45" />

        <!-- First Name -->
        <el-table-column label="FIRST NAME" prop="first_name" min-width="130">
          <template slot-scope="{ row }">
            <span>{{ row.first_name }}</span>
          </template>
        </el-table-column>

        <!-- Last Name -->
        <el-table-column label="LAST NAME" prop="lastname" min-width="130">
          <template slot-scope="{ row }">
            <span>{{ row.lastname }}</span>
          </template>
        </el-table-column>

        <!-- Email -->
        <el-table-column label="EMAIL" prop="email" min-width="220">
          <template slot-scope="{ row }">
            <span>{{ row.email }}</span>
          </template>
        </el-table-column>

        <!-- Account Name -->
        <el-table-column label="ACCOUNT NAME" min-width="170">
          <template slot-scope="{ row }">
            <span>{{ row.account && row.account.company_name ? row.account.company_name : '—' }}</span>
          </template>
        </el-table-column>
         <!-- Role -->
        <el-table-column label="ROLE" prop="role.name" min-width="120">
          <template slot-scope="{ row }">
            <span>{{ row.role && row.role.name ? row.role.name : '—' }}</span>
          </template>
        </el-table-column>

        <!-- Created At -->
        <el-table-column label="CREATED AT" prop="created_at" min-width="160">
          <template slot-scope="{ row }">
            <span>{{ formatLastLogin(row.created_at) }}</span>
          </template>
        </el-table-column>

        <!-- Last Login -->
        <el-table-column label="LAST LOGIN" prop="last_connection" min-width="160">
          <template slot-scope="{ row }">
            <span>{{ formatLastLogin(row.last_connection) }}</span>
          </template>
        </el-table-column>

        <!-- Tools -->
        <el-table-column label="TOOLS" min-width="120">
          <template slot-scope="{ row }">
            <UserTableTools
              :user="row"
              @toggleStatus="onToggleStatus"
              @deleteUser="onDeleteUser"
            />
          </template>
        </el-table-column>
      </el-table>
    </div>
    <div v-if="!hasMoreData && !loading && tableData.length > 0" class="users-table__end-of-list">
      All users loaded
    </div>
  </div>
</template>

<script>
import dayjs from "dayjs";
import OnResize from "@/Extend/onResize";
import UserTableTools from "./UserTableTools.vue";

export default {
  name: "UsersTable",
  mixins: [OnResize],
  components: { UserTableTools },
  props: {
    tableData: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    isLoadingMore: {
      type: Boolean,
      default: false,
    },
    hasMoreData: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      tableHeight: window.innerHeight - 200,
      tableContainer: null,
      tableContainerHeight: 0,
      rowsContainer: null,
    };
  },
  watch: {
    screenWidth() {
      this.tableHeight = window.innerHeight - 200;
    },
  },
  async mounted() {
    await this.sleep(0);
    this.tableContainer = this.$el.querySelector(".el-table__body-wrapper");
    this.tableContainerHeight = this.tableContainer.clientHeight;
    this.tableContainer.addEventListener("scroll", this.infiniteScrollHandler);
    this.rowsContainer = this.$el.querySelector(".el-table__body");
  },
  beforeDestroy() {
    if (this.tableContainer) {
      this.tableContainer.removeEventListener("scroll", this.infiniteScrollHandler);
    }
  },
  methods: {
    infiniteScrollHandler() {
      const elementTableHeight = this.rowsContainer.clientHeight;
      const currentScrollPosition = this.tableContainer.scrollTop;
      if (currentScrollPosition + this.tableContainerHeight < elementTableHeight - 20) return;
      this.tableContainer.scrollTop = currentScrollPosition - 30;
      this.$emit("loadMoreItems");
    },
    sleep(ms) {
      return new Promise((resolve) => setTimeout(resolve, ms));
    },
    formatLastLogin(value) {
      if (!value) return "Sin ingreso";
      const formatted = dayjs(value).format("MM-DD-YYYY HH:mm");
      return formatted === "Invalid Date" ? "Sin ingreso" : formatted;
    },
    onSelectionChange(selection) {
      this.$emit("selectionChange", selection);
    },
    clearSelection() {
      this.$refs.table.clearSelection();
    },
    onToggleStatus(payload) {
      this.$emit("toggleStatus", payload);
    },
    onDeleteUser(userId) {
      this.$emit("deleteUser", userId);
    },
  },
};
</script>

<style lang="scss" scoped>
@use "@/assets/scss/_variables" as *;
@use "@/assets/scss/_mixin" as *;
@use "@/assets/scss/table/_tableAccountManagement" as *;

// ── Table wrapper — aplica el mismo mixin que My Loads ────────
.users-table {
  width: 100%;
  margin-top: 10px;

  &__table {
    @include table-design-book(2);
    position: relative;
    width: 100%;
    margin: auto;
  }

  &__scroll-loading {
    @include loading-overlay;
    align-items: center;
    justify-content: center;
    gap: 10px;
    font-family: $font-family-portal, sans-serif;
    font-size: 13px;
    font-weight: 600;
    color: $color-primary-company;
  }

  &__end-of-list {
    text-align: center;
    padding: 10px 0;
    color: #c0c4cc;
    font-size: 11px;
    font-family: $font-family-portal, sans-serif;
  }

  // ── Status badge ───────────────────────────────────────────
  &__badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 3px 10px;
    border-radius: 20px;
    font-family: $font-family-portal, sans-serif;
    font-size: 11px;
    font-weight: 600;
    white-space: nowrap;

    &--active {
      background-color: #e8f5e9;
      color: #2e7d32;
    }

    &--inactive {
      background-color: #f2f2f2;
      color: #808080;
    }
  }
}

// ── Element UI global overrides (mismo patrón My Loads) ───────
::v-deep {
  .el-table {
    background: transparent;
    font-family: $font-family-portal, sans-serif;
    font-size: 13px;

    &__header {
      th {
        background: #efefef;
        padding-bottom: 8px;
        font-weight: 600;
        font-size: 13px;
        color: $color-primary-company;
        text-align: center;

        .cell {
          display: flex;
          align-items: center;
          justify-content: center;
        }
      }

      th:first-child {
        border-top-left-radius: 25px;
        border-bottom-left-radius: 25px;
      }

      th:last-child {
        border-top-right-radius: 25px;
        border-bottom-right-radius: 25px;
      }
    }

    tr {
      background-color: transparent;
      cursor: pointer;

      .gutter {
        display: none !important;
      }
    }

    &__row {
      .el-table__cell {
        background: white;
        text-align: center;
        border-bottom: 1px solid $color-border-container;
        word-break: normal;
        font-size: 13px;
        font-family: $font-family-portal, sans-serif;
        color: #333333;
      }
    }
  }

  // Checkbox de selección
  .el-checkbox__input.is-checked .el-checkbox__inner,
  .el-checkbox__input.is-indeterminate .el-checkbox__inner {
    background-color: var(--color-border-container) !important;
    border-color: var(--color-border-container) !important;
  }

  // Toggle switch — mismo color que AccountTableTools
  .el-switch.is-checked .el-switch__core {
    border-color: $color-border-container !important;
    background-color: $color-border-container !important;
  }
}
</style>
